import React, { Component, useState, useCallback, useMemo } from "react";
import { Query } from "react-apollo";
import { Col, Input, Row, Icon, Divider, Select } from "antd";
import { Link } from "react-router-dom";
import { css } from "glamor";
import { Tunnel } from "react-tunnels";
import * as R from "ramda";

import Table from "@arachnys/styleguide/Table";
import { withApolloState } from "@arachnys/flow-app/src/util/apollo";
import { authFetch } from "@arachnys/flow-app/src/api";
import ApolloPaginationTable from "../../container/ApolloPaginationTable";
import { DEFAULT_PAGESIZE } from "../../config";
import { debounce } from "../../util";
import {
    normalize,
    countryIsoCodeToName,
    formatDatetimeString,
} from "../../formatters";
import {
    GET_RESEARCH_REQUEST_LABELS,
    SEARCH_RESEARCH_REQUESTS,
} from "./queries";
import MonitoringIcon from "./MonitoringIcon";
import UploadRequestButton from "./BulkRequest/UploadRequestButton";

const styles = {
    loader: css({
        textAlign: "center",
        verticalAlign: "middle",
    }),
    theadTh: css({
        textAlign: "left",
    }),
    searchInput: {
        width: 350,
    },

    requestsThead: css({
        background: "#fff",
        borderBottom: "1px dashed #000",
        borderTop: "none",
        fontSize: "12px",
        padding: "0 0 0 185px",
    }),
    requestsTr: css({
        borderBottom: "1px dashed #000",
        borderTop: "none",
        fontSize: "12px",
        padding: "0 0 0 185px",
    }),
    expanderIcon: css({
        fontSize: "16px",
    }),
    searchIcon: css({
        width: 25,
        opacity: 0.5,
        cursor: "pointer",
    }),
    renewCell: css({
        textAlign: "right",
    }),
    monitoringCell: css({
        textAlign: "center",
    }),
    viewCell: css({
        textAlign: "center",
        fontWeight: "bold",
    }),
    openRequestsCell: css({
        textDecoration: "underline",
        color: "#1b75bc",
    }),
    labelsCell: css({
        whiteSpace: "normal !important",
    }),
};

const withSelect = Select => props => <Select {...props} />;
const editLabels = async (labelsArray, setSaving, ulid, refetch) => {
    setSaving(true);
    const result = await authFetch(
        new Request(`/api/tickets/${ulid}/labels/`, {
            method: "POST",
            body: JSON.stringify({ labels: labelsArray }),
        })
    );
    if (result.ok) {
        refetch();
    } else {
        setSaving(false);
    }
};
const showLabels = labels => () => (labels.length ? labels.join(", ") : "-");

const LabelsEditor = ({ row, canEdit, availableLabels, refetch }) => {
    const [isSaving, setSaving] = useState(false);

    const ulid = useMemo(() => R.pathOr("", ["original", "ulid"], row), [row]);
    const labels = useMemo(() => R.pathOr("", ["original", "labels"], row), [
        row,
    ]);

    const editLabelsCallback = useCallback(
        labelsArray => {
            setSaving(true);
            editLabels(labelsArray, setSaving, ulid, refetch);
        },
        [setSaving, ulid, refetch]
    );
    const MemoizedSelect = useMemo(
        () => (canEdit ? withSelect(Select) : showLabels(labels)),
        [canEdit, labels]
    );

    return (
        <MemoizedSelect
            value={labels}
            onChange={editLabelsCallback}
            mode="multiple"
            style={{ width: "100%" }}
            placeholder="Labels"
            disabled={isSaving}
        >
            {availableLabels.map(l => (
                <Select.Option key={l}>{l}</Select.Option>
            ))}
        </MemoizedSelect>
    );
};

const LabelsEditorQuery = props => {
    return (
        <Query query={GET_RESEARCH_REQUEST_LABELS} fetchPolicy="cache-only">
            {withApolloState(({ data }) => {
                const canEdit = R.pathOr(
                    false,
                    [
                        "ticketingPermissions",
                        "researchRequest",
                        "canChangeLabels",
                    ],
                    data
                );
                const availableLabels = R.pathOr([], ["availableLabels"], data);

                return (
                    <LabelsEditor
                        {...props}
                        canEdit={canEdit}
                        availableLabels={availableLabels}
                    />
                );
            })}
        </Query>
    );
};

const columns = [
    {
        id: "expander",
        expander: true,
        width: 35,
        Expander: row => (
            <Icon
                className={`${styles.expanderIcon}`}
                type={row.isExpanded ? "folder-open" : "folder"}
            />
        ),
    },
    {
        id: "name",
        accessor: "node.name",
        Header: "Request name",
        sortable: false,
        width: 150,
    },
    {
        id: "country",
        Header: "Country",
        sortable: false,
        width: 180,
        accessor: d => countryIsoCodeToName(d.node.countryCode),
    },
    {
        id: "client",
        width: 180,
        accessor: "node.clientName",
        Header: "Client",
        sortable: false,
    },
    {
        id: "openRequests",
        width: 150,
        expander: true,
        Expander: row => {
            const requests = R.pathOr(
                [],
                ["original", "node", "requests"],
                row
            );
            const completed = R.filter(
                R.propEq("status", "COMPLETED"),
                requests
            );
            return (
                <div
                    className={`${styles.openRequestsCell}`}
                >{`${completed.length}/${requests.length}`}</div>
            );
        },
        Header: "Requests",
        sortable: false,
    },
    {
        id: "renewRequest",
        width: 140,
        Header: "",
        Cell: row => {
            const requests = R.pathOr(
                [],
                ["original", "node", "requests"],
                row
            );
            const allCompleted = R.all(R.pathEq(["status"], "COMPLETED"))(
                requests
            );
            return requests.length && allCompleted ? (
                <Link to={`/tickets/${R.last(requests).ulid}/renew/`}>
                    Renew request
                </Link>
            ) : null;
        },
        sortable: false,
        className: `${styles.renewCell}`,
    },
    {
        id: "empty",
        sortable: false,
        Header: "",
    },
    {
        id: "monitoring",
        width: 100,
        Header: "Monitoring",
        Cell: row => {
            const requests = R.pathOr(
                [],
                ["original", "node", "requests"],
                row
            );
            const lastCompleted = R.findLast(req => req.completionDate)(
                requests
            );
            const showMonitoring =
                lastCompleted && lastCompleted.entity.continuousMonitoring;

            return showMonitoring ? (
                <MonitoringIcon request={lastCompleted} />
            ) : null;
        },
        sortable: false,
        className: `${styles.monitoringCell}`,
    },
];

const requestColumns = refetch => [
    {
        id: "created_date",
        width: 120,
        accessor: "createdDate",
        Header: "Created date",
        Cell: row => formatDatetimeString({ str: row.value }),
    },
    {
        id: "requester",
        width: 120,
        Header: "Created by",
        sortable: false,
        accessor: d => {
            return [d.requester.firstName, d.requester.lastName]
                .join(" ")
                .trim();
        },
    },
    {
        id: "requestType",
        width: 120,
        Header: "Request Type",
        sortable: false,
        accessor: d =>
            R.path(["entity", "additionalProperties", "requestType"], d),
    },
    {
        id: "billingCode",
        width: 140,
        Header: "Business Unit",
        sortable: false,
        accessor: d => R.last(R.pathOr([], ["entity", "linesOfBusiness"], d)),
    },
    {
        id: "completionDate",
        width: 145,
        Header: "Status/Completed date",
        sortable: false,
        accessor: d => {
            if (d.status === "COMPLETED")
                return formatDatetimeString({ str: d.completionDate });
            else return normalize((d.status || "").toLowerCase());
        },
    },
    {
        id: "labels",
        width: 150,
        Header: "Labels",
        sortable: false,
        className: `${styles.labelsCell}`,
        Cell: row => <LabelsEditorQuery refetch={refetch} row={row} />,
    },
    {
        id: "empty",
        sortable: false,
        Header: "",
    },
    {
        id: "actions",
        width: 100,
        Header: "",
        sortable: false,
        className: `${styles.viewCell}`,
        Cell: row => (
            <>
                <Link key="view" to={`/tickets/${row.original.ulid}/`}>
                    View
                </Link>
                {row.original.canEdit && (
                    <>
                        <Divider type="vertical" />
                        <Link
                            key="edit"
                            to={`/tickets/${row.original.ulid}/edit/`}
                        >
                            Edit
                        </Link>
                    </>
                )}
            </>
        ),
    },
];

const RequestsTable = ({ data, refetch }) => {
    return (
        <Table
            data={data}
            defaultPageSize={data.length}
            columns={requestColumns(refetch)}
            getTheadProps={() => ({
                className: `${styles.requestsThead}`,
            })}
            getTrProps={() => ({
                className: `${styles.requestsTr}`,
            })}
            showPagination={false}
        />
    );
};

export class SearchPage extends Component {
    state = {
        filters: {},
        expanded: {},
    };

    expandRow = expanded => this.setState({ expanded });

    handleSearchNameFilter = e => {
        this.setFilters({ name: e.target.value });
    };

    handleSortedChange = sortings => {
        const { id, desc } = sortings[0];
        const ordering = (desc ? "-" : "") + id;
        this.setFilters({ ordering });
    };

    refreshTable = () =>
        this.setState({ expanded: {} }, () =>
            this.props.apolloProps.refetch(this.state.filters)
        );

    setFilters = (params = {}) => {
        this.setState(
            {
                filters: {
                    ...this.state.filters,
                    ...params,
                },
            },
            () => {
                this._applyWithDebounce(this.refreshTable);
            }
        );
    };

    _applyWithDebounce = debounce((fn, args) => {
        fn(args);
    }, 500);

    render() {
        return (
            <div>
                <Tunnel id="create-bulk-request">
                    <UploadRequestButton />
                </Tunnel>
                <Tunnel id="bluebar-left-content">
                    <Row>
                        <Col span={8}>
                            <Input.Search
                                placeholder="Search Requests"
                                onChange={this.handleSearchNameFilter}
                                size="large"
                                style={styles.searchInput}
                            />
                        </Col>
                    </Row>
                </Tunnel>
                <ApolloPaginationTable
                    apolloProps={this.props.apolloProps}
                    columns={columns}
                    styles={styles.theadTh}
                    onSortedChange={this.handleSortedChange}
                    onExpandedChange={this.expandRow}
                    expanded={this.state.expanded}
                    SubComponent={row => {
                        const requests = R.pathOr(
                            [],
                            ["original", "node", "requests"],
                            row
                        );

                        return requests.length ? (
                            <RequestsTable
                                data={requests}
                                refetch={this.props.apolloProps.refetch}
                            />
                        ) : null;
                    }}
                />
            </div>
        );
    }
}

const SearchPageQuery = props => (
    <Query
        query={SEARCH_RESEARCH_REQUESTS}
        fetchPolicy="cache-and-network"
        variables={{
            name: props.name,
            count: DEFAULT_PAGESIZE,
            cursor: null,
        }}
    >
        {apolloProps => {
            return <SearchPage {...props} apolloProps={apolloProps} />;
        }}
    </Query>
);

export default SearchPageQuery;
